import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Plus, X, Trash2, Send, Ban, FileDown, Package, Pencil } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  draft: "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]",
  sent: "bg-[hsl(var(--forest)/0.15)] text-[hsl(var(--forest))]",
  cancelled: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

const EMPTY_LINE = { kind: "product", ref_id: "", name: "", unit: "pcs", qty: 1, rate: 0, notes: "" };

export default function DispatchesTab() {
  const [rows, setRows] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | dispatch obj

  const load = async () => {
    setLoading(true);
    try {
      const [d, b, p, inv] = await Promise.all([
        api.get("/staff/dispatches"),
        api.get("/staff/buyers", { params: { active_only: true } }),
        api.get("/staff/finished-products", { params: { active_only: true } }),
        api.get("/staff/inventory/items"),
      ]);
      setRows(d.data);
      setBuyers(b.data);
      setProducts(p.data);
      setInventory(inv.data || []);
    } catch { toast.error("Could not load dispatches"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing("new");
  const openEdit = (d) => setEditing(d);
  const close = () => setEditing(null);

  const send = async (d) => {
    if (!window.confirm(`Mark dispatch ${d.dispatch_number} as SENT? A signed delivery note can still be re-downloaded.`)) return;
    try {
      await api.post(`/staff/dispatches/${d.id}/send`);
      toast.success("Dispatch marked as sent");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };

  const cancel = async (d) => {
    if (!window.confirm(`Cancel dispatch ${d.dispatch_number}? This will remove it from buyer receivables.`)) return;
    try {
      await api.post(`/staff/dispatches/${d.id}/cancel`);
      toast.success("Dispatch cancelled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Cancel failed"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete draft dispatch ${d.dispatch_number}?`)) return;
    try {
      await api.delete(`/staff/dispatches/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const downloadPdf = async (d) => {
    try {
      const token = localStorage.getItem("tbb_token");
      const res = await fetch(`${API}/staff/dispatches/${d.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d.dispatch_number.replaceAll("/", "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "PDF download failed"); }
  };

  return (
    <div data-testid="dispatches-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-[hsl(var(--ink-soft))]">
          {loading ? "Loading…" : `${rows.length} dispatch${rows.length === 1 ? "" : "es"}`}
        </div>
        <button data-testid="dispatch-new" onClick={openNew} disabled={buyers.length === 0} className="btn-brass !py-2 !px-4 text-sm disabled:opacity-50">
          <Plus size={14} /> New dispatch
        </button>
      </div>
      {buyers.length === 0 && !loading && (
        <div className="mt-4 border border-[hsl(var(--brass))]/40 bg-[hsl(var(--brass))]/5 p-3 text-sm">
          Add at least one buyer in the <b>Buyers</b> tab to create dispatches.
        </div>
      )}

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-left text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="px-4 py-2">Dispatch #</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Buyer</th>
              <th className="px-4 py-2 text-right">Lines</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]"><Package className="inline mr-2 opacity-40" size={16} />No dispatches yet.</td></tr>
            )}
            {!loading && rows.map((d) => (
              <tr key={d.id} data-testid={`dispatch-row-${d.id}`} className="border-t border-[hsl(var(--rule))]">
                <td className="px-4 py-2 font-mono text-xs">{d.dispatch_number}</td>
                <td className="px-4 py-2">{d.dispatch_date}</td>
                <td className="px-4 py-2">{d.buyer_name}</td>
                <td className="px-4 py-2 text-right">{(d.lines || []).length}</td>
                <td className="px-4 py-2 text-right font-semibold">{inr(d.subtotal)}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLE[d.status] || ""}`}>{d.status}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    <button data-testid={`dispatch-pdf-${d.id}`} onClick={() => downloadPdf(d)} className="p-1.5 text-[hsl(var(--forest))] hover:underline" title="Delivery note PDF"><FileDown size={13} /></button>
                    <button data-testid={`dispatch-edit-${d.id}`} onClick={() => openEdit(d)} className="p-1.5 hover:text-[hsl(var(--ink))]" title="Edit"><Pencil size={13} /></button>
                    {d.status === "draft" && (
                      <>
                        <button data-testid={`dispatch-send-${d.id}`} onClick={() => send(d)} className="p-1.5 text-[hsl(var(--forest))]" title="Mark as sent"><Send size={13} /></button>
                        <button data-testid={`dispatch-del-${d.id}`} onClick={() => del(d)} className="p-1.5 text-[hsl(6_62%_45%)]" title="Delete draft"><Trash2 size={13} /></button>
                      </>
                    )}
                    {d.status === "sent" && (
                      <button data-testid={`dispatch-cancel-${d.id}`} onClick={() => cancel(d)} className="p-1.5 text-[hsl(6_62%_45%)]" title="Cancel"><Ban size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <DispatchEditor
          initial={editing === "new" ? null : editing}
          buyers={buyers}
          products={products}
          inventory={inventory}
          onClose={close}
          onSaved={() => { close(); load(); }}
        />
      )}
    </div>
  );
}

function DispatchEditor({ initial, buyers, products, inventory, onClose, onSaved }) {
  const [buyerId, setBuyerId] = useState(initial?.buyer_id || (buyers[0]?.id || ""));
  const [dispatchDate, setDispatchDate] = useState(initial?.dispatch_date || todayISO());
  const [lines, setLines] = useState(initial?.lines?.length ? initial.lines.map((l) => ({ ...l })) : [{ ...EMPTY_LINE }]);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);

  const subtotal = useMemo(() => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0), [lines]);

  const updateLine = (idx, patch) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const pickCatalog = (idx, kind, ref_id) => {
    if (!ref_id) { updateLine(idx, { kind, ref_id: "", name: "", unit: "pcs", rate: 0 }); return; }
    if (kind === "product") {
      const p = products.find((x) => x.id === ref_id);
      if (p) updateLine(idx, { kind, ref_id, name: p.name, unit: p.unit, rate: Number(p.default_rate || 0) });
    } else {
      const it = inventory.find((x) => x.id === ref_id);
      if (it) updateLine(idx, { kind, ref_id, name: it.name, unit: it.unit || "kg", rate: Number(it.last_rate || 0) });
    }
  };

  const save = async () => {
    if (!buyerId) { toast.error("Pick a buyer"); return; }
    const validLines = lines.map((l) => ({
      kind: l.kind || "product",
      ref_id: l.ref_id || "",
      name: (l.name || "").trim(),
      unit: l.unit || "pcs",
      qty: Number(l.qty) || 0,
      rate: Number(l.rate) || 0,
      notes: (l.notes || "").trim(),
    })).filter((l) => l.name && l.qty > 0);
    if (validLines.length === 0) { toast.error("Add at least one valid line"); return; }
    setSaving(true);
    try {
      if (initial) {
        await api.patch(`/staff/dispatches/${initial.id}`, { buyer_id: buyerId, dispatch_date: dispatchDate, lines: validLines, notes });
        toast.success("Dispatch updated");
      } else {
        await api.post("/staff/dispatches", { buyer_id: buyerId, dispatch_date: dispatchDate, lines: validLines, notes });
        toast.success("Dispatch created (draft)");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="dispatch-editor" className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-5xl my-6 shadow-xl">
        <div className="p-4 border-b border-[hsl(var(--rule))] flex items-center justify-between">
          <div>
            <div className="eyebrow">{initial ? "Edit dispatch" : "New dispatch"}</div>
            <h2 className="mt-1 text-xl display">{initial ? initial.dispatch_number : "Create a delivery note"}</h2>
          </div>
          <button onClick={onClose} data-testid="dispatch-editor-close" className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Buyer *
              <select data-testid="dispatch-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="tbb-inp mt-1 w-full">
                <option value="">— Select buyer —</option>
                {buyers.map((b) => <option key={b.id} value={b.id}>{b.name}{b.category ? ` (${b.category})` : ""}</option>)}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Dispatch date *
              <input type="date" data-testid="dispatch-date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} className="tbb-inp mt-1 w-full" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">Line items</div>
              <button data-testid="dispatch-add-line" onClick={addLine} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--forest))] hover:underline">
                <Plus size={12} /> Add line
              </button>
            </div>
            <div className="border border-[hsl(var(--rule))] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--bg-2))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--ink-soft))]">
                  <tr>
                    <th className="px-2 py-1.5 w-24">Kind</th>
                    <th className="px-2 py-1.5">Item</th>
                    <th className="px-2 py-1.5 w-20 text-right">Qty</th>
                    <th className="px-2 py-1.5 w-20">Unit</th>
                    <th className="px-2 py-1.5 w-28 text-right">Rate ₹</th>
                    <th className="px-2 py-1.5 w-28 text-right">Amount ₹</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, idx) => (
                    <tr key={idx} data-testid={`dispatch-line-${idx}`} className="border-t border-[hsl(var(--rule))] align-top">
                      <td className="px-2 py-1.5">
                        <select data-testid={`dispatch-line-kind-${idx}`} value={ln.kind} onChange={(e) => updateLine(idx, { kind: e.target.value, ref_id: "" })} className="tbb-inp !py-1 !px-1.5 text-xs w-full">
                          <option value="product">Finished</option>
                          <option value="inventory">Inventory</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select data-testid={`dispatch-line-ref-${idx}`} value={ln.ref_id} onChange={(e) => pickCatalog(idx, ln.kind, e.target.value)} className="tbb-inp !py-1 !px-1.5 text-xs w-full">
                          <option value="">— pick or type below —</option>
                          {(ln.kind === "product" ? products : inventory).map((x) => (
                            <option key={x.id} value={x.id}>{x.name}</option>
                          ))}
                        </select>
                        <input data-testid={`dispatch-line-name-${idx}`} value={ln.name} onChange={(e) => updateLine(idx, { name: e.target.value, ref_id: "" })} placeholder="or custom name" className="tbb-inp !py-1 !px-1.5 text-xs w-full mt-1" />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input data-testid={`dispatch-line-qty-${idx}`} type="number" min="0" step="0.01" value={ln.qty} onChange={(e) => updateLine(idx, { qty: e.target.value })} className="tbb-inp !py-1 !px-1.5 text-xs text-right w-full" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input data-testid={`dispatch-line-unit-${idx}`} value={ln.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })} className="tbb-inp !py-1 !px-1.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input data-testid={`dispatch-line-rate-${idx}`} type="number" min="0" step="0.01" value={ln.rate} onChange={(e) => updateLine(idx, { rate: e.target.value })} className="tbb-inp !py-1 !px-1.5 text-xs text-right w-full" />
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs font-semibold">
                        {inr(Number(ln.qty || 0) * Number(ln.rate || 0))}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {lines.length > 1 && (
                          <button data-testid={`dispatch-line-remove-${idx}`} onClick={() => removeLine(idx)} className="text-[hsl(6_62%_45%)] hover:opacity-80"><Trash2 size={13} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Notes (optional)
            <textarea rows={2} data-testid="dispatch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="tbb-inp mt-1 w-full resize-none" placeholder="Vehicle no., driver, delivery instructions…" />
          </label>

          <div className="flex items-center justify-between gap-3">
            <div className="text-lg" data-testid="dispatch-subtotal">Subtotal: <span className="font-semibold">{inr(subtotal)}</span></div>
            <div className="flex gap-2">
              <button onClick={onClose} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button data-testid="dispatch-save" onClick={save} disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : (initial ? "Update draft" : "Save as draft")}</button>
            </div>
          </div>
          <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
        </div>
      </div>
    </div>
  );
}
